/* Libs */
import Segment from '../index';

/* Utils */
import {getCartSegmentProperties} from '../utils';

/* Types */
import {Cart, Item} from 'types/cart.types';
import {Sku} from 'types/sku.types';
import {IHash} from 'types/base.types';
import {PartnerAPI} from '@integrations/partners/partners.types';
import {TLandings} from 'types/landings.types';

/**
 * -------------------------------------------------------------------------------------
 * Define the "event name" here, in TRACKING_TYPES
 */
export const TRACKING_TYPES = {
  productViewed: 'Product Viewed',
  productAdded: 'Product Added',
  geoGetAPrice: 'Geo Get A Price',
};

export type TSkuLanding = {
  price: string;
  skus: Sku[];
  startsAtPrice: string;
  seoName: string;
  partner: PartnerAPI;
  images?: {
    front?: {
      original: string;
    };
  };
};
/**
 * -------------------------------------------------------------------------------------
 * These are just "actions" that are consolidated in one place that
 * will return your properties structure. Just "method" out what you
 * need to handle.
 */

export const baseSkuAttributes = (sku: Sku = {} as Sku) => ({
  id: sku.id,
  image_url: sku.image,
  is_remote: sku.remote || false,
  name: sku.name,
  partner: sku.partner,
  price: sku.basePrice / 100,
  sku_id: sku.id,
  base_price: sku.basePrice / 100,
  category: sku.category,
  content_ids: [sku.id],
  product_url: `${window?.location?.origin}${window?.location?.pathname}`,
});

export const SegmentTrackingAction = {
  /* SkuTypes Page Data */

  /* Sku page data */
  skuPage(sku: Sku, additional: IHash<any> = {}) {
    const payload = {
      ...baseSkuAttributes(sku),
      ...additional,
    };

    Segment.track(TRACKING_TYPES.productViewed, payload);
  },
  /* Landing Page Sku */
  skuPageLanding(landing: TLandings) {
    const [firstSku] = landing.skus || {};
    const price = `${landing.startsAtPrice}`.replace(/\D/g, '');
    const name = landing.seoName;
    const partner = landing.partner;

    const payload = {
      ...baseSkuAttributes({
        ...firstSku,
        // @ts-ignore
        price,
        name,
        // @ts-ignore
        partner,
        base_price: price,
        image_url: landing.images?.front?.original,
      }),
    };

    Segment.track(TRACKING_TYPES.productViewed, payload);
  },
  /* This fires when an addtoCart mutation is called */
  skuAddedToCart({cart, sku}: {cart: Cart; sku: Sku}) {
    const cartItem = cart.items.find(i => i.skuId === sku.id);
    const cartBreakdown = cart.breakdown?.items.find((i: Item) => i.skuId === sku.id);

    const partner = sku.partner
      ? {
          partner_id: sku.partner.id,
          partner_name: sku.partner.landingSeoName,
        }
      : null;

    const payload = {
      category: sku.category,
      id: `${sku.id}`,
      content_ids: [sku.id],
      image_url: sku.heroImageUrl,
      is_remote: cartItem?.remote,
      name: sku.name,
      partner,
      sku_id: sku.id,
      cart: getCartSegmentProperties(cart),
      price: cartBreakdown.amount / 100,
      base_price: cartBreakdown.amount / 100,
      physical_product: cartBreakdown.isProduct,
      requires_installation: cartBreakdown.requiresInstallation,
    };

    Segment.track(TRACKING_TYPES.productAdded, payload);
  },

  /* Might use this on product page too */
  getAPriceClickSegment({
    zipCode,
    email,
    treatment,
    serviceName,
    skuId,
  }: {
    serviceName: string;
    skuId: number;
    zipCode: string;
    email: string;
    treatment: string;
  }) {
    const payload = {
      zipCode,
      email,
      treatment,
      skuId,
      skuName: serviceName,
    };

    Segment.track(TRACKING_TYPES.geoGetAPrice, payload);
  },
};
